/**
 * Copyright 2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useRef, useState, useCallback } from "react";
import { ResourceExecutionStatus } from "~/generated/graphql";
import {
  SUCCESS_STATUSES,
  UNSUCCESS_STATUSES,
  NOT_COMPLETED_STATUSES,
} from "~/views/events/constants/resourceExecutionStatus";

export type ResourceStateCounts = {
  succeeded: number;
  failed: number;
  notCompleted: number;
  total: number;
  hasData: boolean;
};

/**
 * Hook to maintain idempotent resource state counts using a map-based approach.
 * Maps: Map<eventActionId, Map<resourceStateId, status>>
 * This prevents race conditions and ensures accurate counts even with concurrent updates.
 */
export function useEventActionResourceCounts() {
  // Map<eventActionId, Map<resourceStateId, status>>
  const actionResourceMapsRef = useRef<
    Map<string, Map<string, ResourceExecutionStatus>>
  >(new Map());

  // Force re-render when counts change
  const [, forceUpdate] = useState({});

  const updateResourceState = useCallback(
    (
      eventActionId: string,
      resourceStateId: string,
      status: ResourceExecutionStatus
    ) => {
      // Get or create the map for this action
      const existingMap = actionResourceMapsRef.current.get(eventActionId);
      const resourceMap =
        existingMap || new Map<string, ResourceExecutionStatus>();

      if (!existingMap) {
        actionResourceMapsRef.current.set(eventActionId, resourceMap);
      }

      // Update the resource state (idempotent - overwrites duplicates)
      resourceMap.set(resourceStateId, status);

      // Force re-render to update counts in UI
      forceUpdate({});
    },
    []
  );

  const initializeActionCounts = useCallback(
    (
      eventActionId: string,
      resourceStates: Array<{ id: string; status: ResourceExecutionStatus }>
    ) => {
      const resourceMap = new Map<string, ResourceExecutionStatus>();

      resourceStates.forEach((resourceState) => {
        resourceMap.set(resourceState.id, resourceState.status);
      });

      actionResourceMapsRef.current.set(eventActionId, resourceMap);
      forceUpdate({});
    },
    []
  );

  const getCountsForAction = useCallback(
    (eventActionId: string): ResourceStateCounts => {
      const resourceMap = actionResourceMapsRef.current.get(eventActionId);
      if (!resourceMap || resourceMap.size === 0) {
        return {
          succeeded: 0,
          failed: 0,
          notCompleted: 0,
          total: 0,
          hasData: false,
        };
      }

      const statuses = Array.from(resourceMap.values());
      return {
        succeeded: statuses.filter((s) => SUCCESS_STATUSES.has(s)).length,
        failed: statuses.filter((s) => UNSUCCESS_STATUSES.has(s)).length,
        notCompleted: statuses.filter((s) => NOT_COMPLETED_STATUSES.has(s))
          .length,
        total: statuses.length,
        hasData: statuses.length > 0,
      };
    },
    []
  );

  const clearAction = useCallback((eventActionId: string) => {
    actionResourceMapsRef.current.delete(eventActionId);
    forceUpdate({});
  }, []);

  const clearAll = useCallback(() => {
    actionResourceMapsRef.current.clear();
    forceUpdate({});
  }, []);

  return {
    updateResourceState,
    getCountsForAction,
    initializeActionCounts,
    clearAction,
    clearAll,
  };
}
